import { cancelledAppointmentsSchema, planningWeekSchema, scheduledAppointmentSchema, scheduledInternalMeetingSchema, } from '@ghp/domain';
import { bearer } from './http.js';
import { queryString } from './query-string.js';
/**
 * Le planning vu du bureau (D21) : poser un rendez-vous, et savoir si l'usager en a été prévenu.
 *
 * ⚠️ **Réservé au back-office côté serveur**, sauf `recordFieldAppointment` : un jeton de tablette
 * prend un `403` partout ailleurs, et c'est voulu (D4).
 */
export function createPlanningEndpoints(http) {
    return {
        /**
         * Pose un rendez-vous sur une demande.
         *
         * ⚠️ **La réponse porte `confirmationQueued`, et l'écran doit le montrer** : `false` n'est pas
         * un échec — le rendez-vous est pris, mais personne n'était joignable. L'avaler laisserait un
         * opérateur croire l'usager prévenu alors qu'il ne l'est pas, et il ne rappellera jamais
         * (M21, consigne du contrôleur).
         */
        scheduleAppointment(accessToken, request) {
            return http.postJson('/planning/appointments', request, scheduledAppointmentSchema, bearer(accessToken));
        },
        /**
         * Pose le rendez-vous qu'un agent a pris **sur place** — la seule route du planning ouverte à
         * la tablette : la première pose d'un contrôle systématique (#268), ou le déplacement d'un
         * créneau en place, quel que soit le type (08/10/2026).
         *
         * ⚠️ **L'`id` est tiré par la tablette** et le rejeu le renvoie tel quel : c'est ce qui permet
         * à l'outbox de rejouer un envoi interrompu sans poser un second rendez-vous.
         */
        recordFieldAppointment(accessToken, request) {
            return http.postJson('/planning/field-appointments', request, scheduledAppointmentSchema, bearer(accessToken));
        },
        /**
         * Annule les rendez-vous en vigueur d'une demande — l'usager s'est décommandé.
         *
         * ⚠️ **`POST … /cancel`, et non `DELETE`** : rien n'est supprimé. La ligne reste, marquée
         * annulée, ce qui sépare « annulé » de « manqué » pour l'écran « Relances ».
         *
         * ⚠️ **`cancelled: 0` n'est pas un échec** : la demande n'avait pas de rendez-vous en
         * vigueur. L'écran le dit plutôt que d'annoncer une annulation qui n'a rien annulé (M21).
         *
         * `notify` : « Prévenir l'usager par e-mail » (#573), faux par défaut.
         */
        cancelAppointments(accessToken, { inquiryId, notify = false }) {
            return http.postJson('/planning/appointments/cancel', { inquiryId, notify }, cancelledAppointmentsSchema, bearer(accessToken));
        },
        /**
         * La fenêtre que la grille affiche : les rendez-vous et les points internes qui y tombent.
         *
         * ⚠️ **Pas d'enveloppe paginée**, contrairement aux listes du bureau : une grille de semaine
         * montre tous ses créneaux ou elle ment. Le raisonnement vit en tête de `planning-week.ts` ;
         * ce qui borne la lecture est la fenêtre, et le serveur la ramène dans ses bornes.
         *
         * ⚠️ **Le rail « À planifier » n'est pas ici.** Les demandes sans rendez-vous se demandent au
         * tableau des demandes, sous le filtre `appointment: 'unscheduled'` — c'est le même ensemble
         * de lignes, avec la pagination et les compteurs que cette route a déjà. Une seconde source
         * finirait par afficher un rail qui contredit l'onglet.
         */
        week(accessToken, request = {}) {
            return http.get(`/planning/week${queryString(request)}`, planningWeekSchema, bearer(accessToken));
        },
        /**
         * Pose un point interne sur le créneau d'une équipe.
         *
         * ⚠️ **Rien à annoncer en retour, contrairement à un rendez-vous** : pas de
         * `confirmationQueued`, parce qu'aucun e-mail ne part — un point interne ne concerne personne
         * hors de GHP. La réponse est le point lui-même.
         */
        /** Modifie un point interne posé (#432) — le même corps que la pose, tout entier. */
        updateInternalMeeting(accessToken, id, request) {
            return http.patchJson(`/planning/internal-meetings/${id}`, request, scheduledInternalMeetingSchema, bearer(accessToken));
        },
        /** Supprime un point interne (#432), et le rend. */
        deleteInternalMeeting(accessToken, id) {
            return http.deleteJson(`/planning/internal-meetings/${id}`, scheduledInternalMeetingSchema, bearer(accessToken));
        },
        scheduleInternalMeeting(accessToken, request) {
            return http.postJson('/planning/internal-meetings', request, scheduledInternalMeetingSchema, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=planning.js.map